import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UserPlus, Loader2, Trash2, ShieldCheck, ShieldOff, KeyRound, X } from "lucide-react";
import { errorDetail } from "@/lib/utils";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/users");
        if (!cancelled) setUsers(data);
      } catch (e) {
        setError(errorDetail(e) || "Failed to load users");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  if (me?.role !== "owner") {
    return (
      <AdminLayout>
        <div className="heda-panel mx-auto max-w-lg p-8 text-center md:p-10">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
            <ShieldOff size={26} className="text-heda-olive" />
          </div>
          <div className="hed-overline text-red-700">Owner access only</div>
          <h2 className="mt-1 font-display text-xl font-bold text-heda-ink">Restricted page</h2>
          <p className="mt-1 text-sm text-heda-mute">
            Only owner-level accounts can manage staff users.
          </p>
        </div>
      </AdminLayout>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", form);
      setShowCreate(false);
      setForm({ email: "", name: "", password: "", role: "staff" });
      setTick((t) => t + 1);
    } catch (e) {
      setError(errorDetail(e) || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      setTick((t) => t + 1);
    } catch (e) {
      alert(errorDetail(e) || "Failed");
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email}? (min 8 chars)`);
    if (!pw) return;
    try {
      await api.patch(`/users/${u.id}`, { password: pw });
      alert("Password updated.");
    } catch (e) {
      alert(errorDetail(e) || "Failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setTick((t) => t + 1);
    } catch (e) {
      alert(errorDetail(e) || "Failed");
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="hed-overline">Team</div>
          <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">Users & access</h1>
          <p className="mt-1 max-w-xl text-[13px] text-heda-sub">
            Add staff who can upload and edit products. Owners can do everything; staff cannot delete products or manage users.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="add-user-button"
          className="hed-btn-gold"
        >
          <UserPlus size={14} /> Add user
        </button>
      </div>

      {loading ? (
        <div className="heda-card flex items-center justify-center py-12">
          <Loader2 size={22} className="animate-spin text-heda-gold" />
        </div>
      ) : (
        <div className="heda-card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="heda-thead">
              <tr>
                <th className="px-3 py-3 sm:px-4">User</th>
                <th className="px-3 py-3 sm:px-4">Role</th>
                <th className="hidden px-4 py-3 sm:table-cell">Status</th>
                <th className="px-3 py-3 text-right sm:px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-heda-line">
                  <td className="min-w-0 px-3 py-2.5 sm:px-4">
                    <div className="font-medium text-heda-ink">{u.name || "—"}</div>
                    <div className="text-[11.5px] text-heda-mute [overflow-wrap:anywhere]">{u.email}</div>
                  </td>
                  <td className="px-3 py-2.5 sm:px-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        u.role === "owner"
                          ? "bg-heda-gold/15 text-heda-gold"
                          : "bg-heda-olive-tint text-heda-olive-dark"
                      }`}
                    >
                      {u.role}
                    </span>
                    {u.active === false && (
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-red-700 sm:hidden">Disabled</div>
                    )}
                  </td>
                  <td className="hidden px-4 py-2.5 sm:table-cell">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        u.active === false ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {u.active === false ? "Disabled" : "Active"}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 sm:px-4">
                    <div className="flex items-center justify-end">
                      <button
                        title="Reset password"
                        onClick={() => resetPassword(u)}
                        className="hed-icon-btn"
                        data-testid={`user-reset-${u.id}`}
                      >
                        <KeyRound size={15} />
                      </button>
                      <button
                        title={u.active === false ? "Enable" : "Disable"}
                        onClick={() => toggleActive(u)}
                        className="hed-icon-btn"
                        data-testid={`user-toggle-${u.id}`}
                      >
                        {u.active === false ? (
                          <ShieldCheck size={15} className="text-emerald-700" />
                        ) : (
                          <ShieldOff size={15} />
                        )}
                      </button>
                      <button
                        title="Delete user"
                        onClick={() => del(u)}
                        className="hed-icon-btn hover:bg-red-50 hover:text-red-700"
                        data-testid={`user-delete-${u.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-sm text-heda-mute">
                    No users yet. Click &ldquo;Add user&rdquo; to invite your first staff.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create user modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="w-full max-w-md animate-fade-up overflow-hidden rounded-2xl border border-heda-line bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
              <div>
                <div className="hed-overline">Invite</div>
                <h3 className="font-display text-lg font-bold text-heda-ink">Add a new user</h3>
              </div>
              <button onClick={() => setShowCreate(false)} className="rounded-full p-1.5 text-heda-mute hover:bg-heda-cream/60">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={create} className="space-y-4 p-5">
              <Field label="Full name">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="new-user-name"
                  className="heda-input"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="new-user-email"
                  className="heda-input"
                />
              </Field>
              <Field label="Temporary password (share with them; they can change later)">
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={8}
                  data-testid="new-user-password"
                  className="heda-input"
                />
              </Field>
              <Field label="Role">
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="new-user-role"
                  className="heda-input"
                >
                  <option value="staff">Staff — upload & edit products</option>
                  <option value="owner">Owner — full control</option>
                </select>
              </Field>
              {error && (
                <div className="heda-error">{error}</div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="create-user-button"
                className="heda-btn w-full"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                Create user
              </button>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="heda-label">{label}</label>
      {children}
    </div>
  );
}
